import React, {
  useState, useEffect, useMemo,
} from "react";
import { Table } from "antd";
import { LoadingOutlined, DownloadOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import moment from "moment";
import { getPresignedURL, downloadFileFromS3 } from "../../../utils/helpers";
import { s3AutomationPrivate } from "../../../utils/aws";
import useAbortController from "../../Common/hooks/useAbortController";
import "./InvoiceHistory.css";

/**
 * Extract filename from S3 URL path
 * @param {string} s3Url - The S3 URL path
 * @returns {string} The filename
 */
const extractFileName = (s3Url) => {
  if (!s3Url) return "";
  const parts = s3Url.split("/");
  return parts[parts.length - 1] || "";
};

/**
 * Format date to MM/DD/YYYY, h:mm:ss a format
 * Uses the same logic as QC tab - formats the date as-is without timezone conversion
 * @param {string} dateString - Date string from API
 * @returns {string} Formatted date string
 */
const formatDate = (dateString) => {
  if (!dateString) return "-";
  const time = moment(dateString).format("MM/DD/YYYY, h:mm:ss a");
  return time;
};

/**
 * InvoiceHistory component - displays invoice history table
 * Processes raw response data from parent
 * @param {Object} props - Component props
 * @param {Object} props.response - Raw API response from parent
 * @param {boolean} props.loading - Loading state from parent
 */
export default function InvoiceHistory({ historyData, loading }) {
  const [fileLoadingId, setFileLoadingId] = useState(null); // Use record ID instead of index
  const { setNewAbortController } = useAbortController();

  // Pagination state
  const defaultPageSize = 100;
  const [paginationConfig, setPaginationConfig] = useState({
    pageSize: defaultPageSize,
    total: 0,
    current: 1,
    pageSizeOptions: ["10", "25", "50", "100", "200"],
  });

  // Process response data
  const invoiceHistory = useMemo(() => {
    if (!historyData) return [];
    if (historyData?.success) {
      return Array.isArray(historyData.data?.invoice_history) ? historyData.data.invoice_history : [];
    }
    if (!historyData?.canceled) {
      toast.error(historyData?.message || "Failed to fetch invoice history");
    }
    return [];
  }, [historyData]);

  // Update pagination when data changes
  useEffect(() => {
    if (!historyData) return;

    if (historyData.success) {
      const invoiceList = Array.isArray(historyData.data?.invoice_history) ? historyData.data.invoice_history : [];
      setPaginationConfig((prevState) => ({
        ...prevState,
        total: invoiceList.length,
        current: 1,
      }));
    } else if (!historyData.canceled) {
      setPaginationConfig((prevState) => ({
        ...prevState,
        total: 0,
        current: 1,
      }));
    }
  }, [historyData]);

  /**
   * Handle pagination change
   * @param {number} page - Current page number
   * @param {number} pageSize - Items per page
   */
  const handlePageChange = (page, pageSize) => {
    setPaginationConfig((prevState) => ({
      ...prevState,
      current: page,
      pageSize,
    }));
  };

  /**
   * Handle file download
   * @param {Object} record - The invoice history record
   */
  const handleDownload = async (record) => {
    if (!record.s3_url) {
      toast.error("File URL not available");
      return;
    }

    // Use id as unique identifier for loading state
    const recordId = record.id;

    // Prevent clicking same link while downloading
    if (fileLoadingId === recordId) {
      return;
    }

    const newAbortController = setNewAbortController();

    setFileLoadingId(recordId);
    // Get presigned URL for the S3 path
    const presignedData = await getPresignedURL({
      action: "get",
      urls: [{
        Bucket: s3AutomationPrivate.bucket,
        Name: record.s3_url,
        Region: s3AutomationPrivate.region,
      }],
    }, newAbortController.signal);

    if (presignedData.success && presignedData.result && presignedData.result[record.s3_url]) {
      const fileName = extractFileName(record.s3_url);

      const downloadResp = await downloadFileFromS3(
        record.s3_url,
        s3AutomationPrivate,
        fileName,
        presignedData.result[record.s3_url],
        newAbortController.signal,
      );

      // Only show error if not aborted
      if (!downloadResp.success && !downloadResp.aborted) {
        toast.error(`Failed to download ${fileName}`);
      }
    } else if (!presignedData.aborted) {
      toast.error("Failed to get presigned URL for download");
    }
    // Only clear if the same row is still active and not aborted
    if (!presignedData.aborted) {
      setFileLoadingId((currentId) => (currentId === recordId ? null : currentId));
    }
  };

  const columns = [
    {
      title: "File",
      key: "file",
      className: "sendReportsFilePath",
      render: (_, record) => {
        const fileName = extractFileName(record.s3_url);
        const recordId = record.id;
        const isDownloading = fileLoadingId === recordId;
        return (
          <div className="SendReportAuto__FileName">
            <div className="SendReportAuto__FileName__Left">
              <p className={isDownloading ? "InvoiceHistory__FileName--disabled" : ""}>{fileName}</p>
              {isDownloading ? (
                <LoadingOutlined />
              ) : (
                <DownloadOutlined className="InvoiceHistory__DownloadIcon" />
              )}
            </div>
          </div>
        );
      },
      onCell: (record) => {
        const recordId = record.id;
        return {
          onClick: () => {
            // Prevent clicking if already downloading
            if (fileLoadingId !== recordId) {
              handleDownload(record);
            }
          },
          style: {
            cursor: fileLoadingId === recordId ? "not-allowed" : "pointer",
          },
        };
      },
    },
    {
      title: "Invoice Type",
      dataIndex: "invoice_type",
      key: "invoice_type",
    },
    {
      title: "Created By",
      key: "created_by",
      render: (_, record) => record.created_by_name || "-",
    },
    {
      title: "Created At",
      key: "created_at",
      render: (_, record) => {
        // Use created_at_display if available (already formatted by backend),
        // otherwise use created_at_pacific, then created_at as fallback
        // Same approach as QC tab - use the field directly
        const dateString = record.created_at;
        return formatDate(dateString);
      },
    },
  ];

  return (
    <div className="InvoiceHistoryContainer">
      <Table
        dataSource={invoiceHistory}
        columns={columns}
        rowKey="id"
        loading={loading}
        scroll={{ y: "60vh" }}
        sticky
        pagination={{
          ...paginationConfig,
          onChange: handlePageChange,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
        }}
        bordered
        className="InvoiceHistoryTable"
      />
    </div>
  );
}
